<template>
    <simple-layout>
        <basic-card>
            <template slot="header">
                Exchange rates matrix
            </template>

            <form-wrapper>
                <form-multi-select-input v-model="matrixCurrencies"
                                         caption="Select currency"
                                         :options="currencies.map(ccy => ({name: ccy.code, id: ccy.code}))"/>
                <form-date-input caption="Valid date" v-model="matrixDate"/>
            </form-wrapper>
            <currency-matrix
              :currencies="matrixCurrencies"
              :exchange-date="matrixDate"/>


        </basic-card>
        <basic-card>
            <template slot="header">
                Exchange rates
            </template>
            <toolbox>
                <toolbox-button :help-text="`${$t('searchQuery.exportExcel')}`"
                                @press="$modal.show('exportIntervalModal')">
                    <icon :icon="['fal', 'file-excel']" size="lg"/>
                </toolbox-button>
            </toolbox>
            <data-table
              :configuration="exchangeRateTableConfig"
              :data="exchangeRates"
              component-id="exchangeRateTable"
              @focusedChanged="handleFocusedChanged"
            >
                <template v-if="focused">
                    <basic-card>
                        <template slot="header">
                            Rate values
                        </template>
                        <data-table
                          :data="exchangeRateValues"
                          :configuration="exchangeRateValuesTable"
                        />
                    </basic-card>
                </template>
            </data-table>
        </basic-card>

        <v-modal name="exportIntervalModal"
                 classes="transparent-modal"
                 style=" background-color: rgba(10,10,10,0.66)">
            <basic-card style="min-height: 400px; box-shadow: none !important;">
                <form-wrapper :form-title="$t('exportInterval')">
                    <form-date-input :caption="$t('dateFrom')" v-model="exportDateFrom"/>
                    <form-date-input :caption="$t('dateThru')" v-model="exportDateThru"/>
                </form-wrapper>

                <simple-button :disabled="!exportDateFrom || !exportDateThru" @press="handleExportExcel">{{
                        $t('startExport')
                    }}
                </simple-button>


            </basic-card>
        </v-modal>
    </simple-layout>
</template>

<script>
import SimpleLayout from "@/components/layout/SimpleLayout";
import BasicCard from "@/components/layout/BasicCard";
import DataTable from "@/components/table/DataTable";
import { mapGetters } from "vuex";
import CurrencyMatrix from "@/components/CurrencyMatrix/CurrencyMatrix";
import FormMultiSelectInput from "@/components/form/FormMultiSelectInput";
import FramedContent from "@/components/layout/FramedContent";
import FormWrapper from "@/components/form/FormWrapper";
import FormDateInput from "@/components/form/FormDateInput";
import { isInInterval } from "@/utils/date-utils";
import handleTableExport from "@/utils/excel-utils";
import Toolbox from "@/components/toolbox/Toolbox";
import ToolboxButton from "@/components/toolbox/ToolboxButton";
import SimpleButton from "@/components/button/SimpleButton";

export default {
    name: "ExchangeRatesView",
    components: {
        SimpleButton,
        ToolboxButton,
        Toolbox,
        FormDateInput,
        FramedContent, FormWrapper, FormMultiSelectInput, CurrencyMatrix, DataTable, BasicCard, SimpleLayout
    },
    data: function () {
        return {
            focused: null,
            exchangeRateValues: [],
            matrixCurrencies: ["CZK", "EUR", "USD", "GBP", "HUF", "BGN"],
            matrixDate: new Date(),
            exchangeRateValuesTable: {
                columns: [
                    {
                        caption: "Id",
                        datum: d => d.id
                    },
                    {
                        caption: "Date",
                        datum: d => this.$formats.formatDate(d.date)
                    },
                    {
                        caption: "Units",
                        datum: d => d.units
                    },
                    {
                        caption: "Value",
                        datum: d => this.$formats.formatAmount(d.value / 1000)
                    }
                ],
                enableAdd: true,
                allowEdit: true,
            },
            exchangeRateTableConfig: {
                columns: [
                    {
                        caption: "Id",
                        datum: d => d.id
                    },
                    {
                        caption: "Priority",
                        datum: d => d.priority
                    },
                    {
                        caption: "Primary CCY",
                        datum: d => `1${d.primaryCurrency}`
                    },
                    {
                        caption: "Secondary CCY",
                        datum: d => `${d.secondaryAmount}${d.secondaryCurrency}`
                    },
                    {
                        caption: "Source",
                        datum: d => d.source
                    },
                    {
                        caption: "System name",
                        datum: d => d.systemName
                    }
                ],
                enableAdd: true,
                allowEdit: true,
            },
            exportDateFrom: '2022-03-21T00:00:00',
            exportDateThru: '2022-03-26T00:00:00',
        }
    },
    computed: {
        ...mapGetters({exchangeRates: "exchangeRates/exchangeRates", currencies: "currencies/currencies"})
    },
    methods: {
        async handleFocusedChanged(exchangeRate) {
            this.focused = exchangeRate;
            if (exchangeRate) {
                this.exchangeRateValues = await this.$store.dispatch("exchangeRates/fetchValues", exchangeRate);
            } else {
                this.exchangeRateValues = [];
            }
        },
        async handleExportExcel() {

            try {
                const exchangeRatesValues = await Promise.all(this.exchangeRates.map(exchangeRate => this.$store.dispatch("exchangeRates/fetchValues", exchangeRate)));

                const exchangeRatesValuesFiltered = exchangeRatesValues.map(values => values.filter(isInInterval('date', this.exportDateFrom, this.exportDateThru)))

                const exportedData = exchangeRatesValuesFiltered.flatMap(filteredValues => {

                    if (filteredValues.length) {
                        const exchangeRate = this.exchangeRates.find(exchangeRate => exchangeRate.id === filteredValues[0].exchangeRateId);

                        return filteredValues.map(value => ({
                            date: value.date,
                            primaryCurrency: exchangeRate.primaryCurrency,
                            secondaryAmount: exchangeRate.secondaryAmount,
                            secondaryCurrency: exchangeRate.secondaryCurrency,
                            source: exchangeRate.source,
                            value: value.value,

                        }));

                    } else {
                        return []
                    }
                });

                const columns = [
                    this.exchangeRateValuesTable.columns[1], // this.$formats.formatDate(d.date)
                    this.exchangeRateTableConfig.columns[2], // `1${d.primaryCurrency}`
                    this.exchangeRateTableConfig.columns[3], // `${d.secondaryAmount}${d.secondaryCurrency}`
                    this.exchangeRateTableConfig.columns[4], // d.source
                    this.exchangeRateValuesTable.columns[3], // this.$formats.formatAmount(d.value / 1000)
                ];

                await handleTableExport(columns, exportedData, `menove_kurzy`);

                this.$modal.hide('exportIntervalModal');
                this.$notify({
                    group: 'application',
                    title: this.$t('exportSuccess'),
                    text: this.$t('exportSuccess'),
                    type: "ok"
                })
            } catch (error) {
                console.warn(error)
                this.$notify({
                    group: 'application',
                    title: this.$t('exportError'),
                    text: this.$t('exportError'),
                    type: "error"
                })
            }
        },
    },
    mounted() {
        this.$store.dispatch("exchangeRates/refreshExchangeRates")
    }
}
</script>

<style scoped>

</style>